import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Store, ShieldCheck } from 'lucide-react';
import { homePathFor, useAuth } from '../lib/auth';
import { ApiError, errorMessage } from '../lib/api';
import { demoAvailable, usePublicConfig } from '../lib/config';
import { Wordmark } from '../components/Brand';
import { Print } from './site/Prints';
import { STUDIO_PRINT } from './site/showcase';
import { ErrorSummary, Field, type SummaryItem } from '../components/Form';
import { usePageTitle } from '../components/Page';
import { Loading, Spinner } from '../components/States';
import type { MeResponse, Role } from '../lib/types';

function safeNext(next: string | null, me: MeResponse | null): string {
  const home = homePathFor(me);
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return home;
  // Only return to the area that matches the signed-in role.
  if (me?.user.role === 'admin' && next.startsWith('/admin')) return next;
  if (me?.user.role === 'merchant' && (next.startsWith('/app') || next.startsWith('/personalize/') || next.startsWith('/shopify/onboarding?'))) return next;
  return home;
}

export function LoginPage() {
  usePageTitle('Sign in');
  const { status, me, login, devLogin, sessionExpired, transitionPending } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const config = usePublicConfig();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<SummaryItem[]>([]);
  const [pending, setPending] = useState(false);
  const [demoPending, setDemoPending] = useState<Role | null>(null);
  const [demoError, setDemoError] = useState<string | null>(null);

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Loading label="Checking your session…" />
      </div>
    );
  }
  if (status === 'authenticated' && me && !pending && !demoPending) {
    return <Navigate to={safeNext(params.get('next'), me)} replace />;
  }

  const fieldError = (field: string) => errors.find((e) => e.field === field)?.message ?? null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const found: SummaryItem[] = [];
    if (!email.trim()) found.push({ field: 'login-email', message: 'Enter your email address.' });
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) found.push({ field: 'login-email', message: 'Enter a valid email address.' });
    if (!password) found.push({ field: 'login-password', message: 'Enter your password.' });
    setErrors(found);
    if (found.length) return;
    setPending(true);
    try {
      const result = await login(email.trim(), password);
      const destination = safeNext(params.get('next'), result);
      // Shopify onboarding is a server-rendered, browser-bound page. A full navigation
      // preserves its HttpOnly binding cookie and must bypass the React catch-all route.
      if (destination.startsWith('/shopify/onboarding?')) window.location.assign(destination);
      else navigate(destination, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError && (err.status === 401 || err.status === 400)
          ? 'The email or password is incorrect.'
          : err instanceof ApiError && err.status === 429
            ? 'Too many sign-in attempts. Wait a few minutes and try again.'
            : errorMessage(err);
      setErrors([{ message }]);
    } finally {
      setPending(false);
    }
  };

  const enterDemo = async (role: Role) => {
    setDemoError(null);
    setDemoPending(role);
    try {
      const result = await devLogin(role);
      navigate(safeNext(params.get('next'), result), { replace: true });
    } catch (err) {
      setDemoError(
        err instanceof ApiError && (err.status === 404 || err.status === 403)
          ? 'Demo entry is disabled on this server. It only works in local demo mode on this machine.'
          : errorMessage(err),
      );
    } finally {
      setDemoPending(null);
    }
  };

  const showDemo = demoAvailable(config.data);

  return (
    <div className="standalone">
      <GalleryAside />

      <main className="standalone__form">
        <div className="standalone__card stack">
          {sessionExpired ? (
            <div className="callout callout--warning" role="status">
              <span>Your session ended. Sign in again to continue.</span>
            </div>
          ) : null}
          {params.get('invite') === 'accepted' ? (
            <div className="callout callout--success" role="status">
              <span>Your account is ready. Sign in with your new password.</span>
            </div>
          ) : null}
          <div>
            <h1 className="standalone__title">Sign in to Perzo</h1>
            <p className="standalone__lede">Manage your styles, products and personalized orders.</p>
          </div>

          <ErrorSummary title="Sign-in failed" items={errors} />

          <form className="form" onSubmit={submit} noValidate aria-busy={pending}>
            <Field label="Email" error={fieldError('login-email')}>
              <input id="login-email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Password" error={fieldError('login-password')}>
              <input id="login-password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={transitionPending}>
              {pending ? <Spinner size={16} /> : null}
              {pending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="small muted">
            Installed Perzo from Shopify? Finish the account setup that opened after installation, or sign in here with your Perzo password. New store?{' '}
            <Link to="/get-started">Get started</Link>.
          </p>

          {showDemo ? (
            <section className="demo-entry" aria-labelledby="demo-title">
              <div className="row row--between">
                <h2 id="demo-title" className="demo-entry__title">
                  Local demo
                </h2>
                <span className="badge badge--warning">Simulated</span>
              </div>
              <p className="small muted">
                Seeded demo accounts on this machine only. Generations are simulated, no AI provider is called and nothing is charged. Not available in
                production.
              </p>
              {demoError ? (
                <div className="callout callout--danger" role="alert">
                  <span>{demoError}</span>
                </div>
              ) : null}
              <div className="row">
                <button type="button" className="btn btn--secondary" onClick={() => void enterDemo('merchant')} disabled={transitionPending}>
                  {demoPending === 'merchant' ? <Spinner size={14} /> : <Store size={14} aria-hidden="true" />}
                  Demo merchant
                </button>
                <button type="button" className="btn btn--secondary" onClick={() => void enterDemo('admin')} disabled={transitionPending}>
                  {demoPending === 'admin' ? <Spinner size={14} /> : <ShieldCheck size={14} aria-hidden="true" />}
                  Demo admin
                </button>
              </div>
            </section>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/** The art side: one finished print hung on the studio wall. */
export function GalleryAside({ caption = 'Finished print · ink and wash' }: { caption?: string }) {
  return (
    <aside className="standalone__art">
      <Link to="/" className="standalone__home" aria-label="Perzo home">
        <Wordmark />
      </Link>
      <div className="standalone__wall">
        <Print piece={STUDIO_PRINT} half="full" eager className="standalone__print" />
      </div>
      <p className="standalone__caption">
        <strong>{caption}</strong>
        <span>Illustrative example of a print made from a shopper’s photo.</span>
      </p>
    </aside>
  );
}
